import React, { useEffect, useMemo, useState } from "react";
import TablePagination from "../../components/shared/TablePagination";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { Modal } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { getApiErrorMessage } from "../../store/api/baseApi";
import { exportToExcel } from "../../utils/exportExcel";
import {
  useDeleteProjectMutation,
  useFetchProjectsQuery,
  useUpdateProjectMutation,
} from "../../store/api/projectsApi";
import { ProjectRecord, ProjectStatus, getProjectProgressSummary } from "./adminData";
import "./admin.css";

const ProjectListPage = () => {
  const navigate = useNavigate();
  const { data: projects = [], isLoading, error } = useFetchProjectsQuery();
  const [updateProject, { isLoading: isUpdating }] = useUpdateProjectMutation();
  const [deleteProject, { isLoading: isDeleting }] = useDeleteProjectMutation();
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProject, setSelectedProject] = useState<ProjectRecord | null>(null);
  const [actionError, setActionError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  const filteredProjects = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return projects.filter((project) => {
      const matchesStatus = statusFilter === "all" || project.status === statusFilter;
      const matchesQuery =
        !query ||
        project.title.toLowerCase().includes(query) ||
        project.owner.toLowerCase().includes(query) ||
        project.location.toLowerCase().includes(query);

      return matchesStatus && matchesQuery;
    });
  }, [projects, searchQuery, statusFilter]);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / PAGE_SIZE));
  const pagedProjects = filteredProjects.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const pendingCount = projects.filter((project) => project.status === "pending").length;
  const activeCount = projects.filter((project) => project.status === "in-progress").length;
  const completedCount = projects.filter((project) => project.status === "completed").length;

  const handleStatusChange = async (project: ProjectRecord, status: ProjectStatus) => {
    setActionError("");

    try {
      await updateProject({
        id: project.id,
        payload: {
          title: project.title,
          owner: project.owner,
          location: project.location,
          area: project.area,
          rateOfConstruction: project.rateOfConstruction,
          description: project.description,
          status,
          contractFiles: project.contractFiles,
        },
      }).unwrap();
    } catch (apiError) {
      setActionError(getApiErrorMessage(apiError as FetchBaseQueryError));
    }
  };

  const handleDelete = async (project: ProjectRecord) => {
    const confirmed = window.confirm(`Delete project "${project.title}"? This cannot be undone.`);

    if (!confirmed) {
      return;
    }

    setActionError("");

    try {
      await deleteProject(project.id).unwrap();

      if (selectedProject?.id === project.id) {
        setSelectedProject(null);
      }
    } catch (apiError) {
      setActionError(getApiErrorMessage(apiError as FetchBaseQueryError));
    }
  };

  const busy = isUpdating || isDeleting;
  const errorMessage = error ? getApiErrorMessage(error as FetchBaseQueryError) : "";

  return (
    <section className='admin-page'>
      <div className='container-fluid px-0'>
        <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px #0000000a" }}>
          {/* Gradient header */}
          <div style={{ background: "linear-gradient(135deg, #134e4a, #0d9488, #14b8a6)", padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className='bi bi-kanban-fill' style={{ color: "#fff", fontSize: 14 }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#fff", lineHeight: 1.2 }}>Project List</div>
                <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)" }}>Filter, review and manage all project records</div>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              <Link to='/admin/projects/new' style={{ background: "rgba(255,255,255,0.9)", color: "#0d9488", borderRadius: 8, padding: "4px 12px", fontSize: "0.8rem", fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 5 }}>
                <i className='bi bi-plus-lg' /> Add Project
              </Link>
              <Link to='/admin/projects/progress' style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", borderRadius: 8, padding: "4px 12px", fontSize: "0.8rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 5 }}>
                <i className='bi bi-list-check' /> Progress
              </Link>
            </div>
          </div>
          {/* Filter bar */}
          <div className='d-flex flex-wrap gap-3 align-items-center p-3 border-bottom'>
            <div style={{ minWidth: 240 }}>
              <input
                className='form-control form-control-sm'
                placeholder='Search by title, owner, or location…'
                title='Search projects'
                aria-label='Search by project, owner, or location'
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </div>
            <div style={{ minWidth: 160 }}>
              <select className='form-select form-select-sm' title='Filter by status' aria-label='Filter by status' value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value='all'>All Statuses</option>
                <option value='pending'>Pending</option>
                <option value='in-progress'>In Progress</option>
                <option value='completed'>Completed</option>
              </select>
            </div>
            <button type='button' className='btn btn-outline-success btn-sm d-inline-flex align-items-center gap-2'
              onClick={() => exportToExcel(filteredProjects.map((p) => ({ Title: p.title, Owner: p.owner, Location: p.location, "Area (sq ft)": p.area, "Rate of Construction": p.rateOfConstruction, Status: p.status, "Created At": p.createdAt })), "Projects")}>
              <i className='bi bi-file-earmark-excel' /> Export
            </button>
            <span className='small text-muted ms-auto'>
              {filteredProjects.length} project{filteredProjects.length !== 1 ? "s" : ""}
              {' · '}
              <span className='text-warning fw-semibold'>{pendingCount} pending</span>
              {' · '}
              <span className='text-primary fw-semibold'>{activeCount} in progress</span>
              {' · '}
              <span className='text-success fw-semibold'>{completedCount} completed</span>
            </span>
          </div>
          <div className='card-body p-4'>
            {isLoading ? <div className='alert alert-light border'>Loading projects...</div> : null}
            {errorMessage ? <div className='alert alert-danger'>{errorMessage}</div> : null}
            {actionError ? <div className='alert alert-danger'>{actionError}</div> : null}

            {!isLoading && !errorMessage ? (
              <>
                <div className='table-responsive'>
                  <table className='table align-middle mb-0'>
                    <thead className='table-light'>
                      <tr>
                        <th scope='col'>#</th>
                        <th scope='col'>Project</th>
                        <th scope='col'>Owner</th>
                        <th scope='col'>Location</th>
                        <th scope='col'>Area</th>
                        <th scope='col'>Rate</th>
                        <th scope='col'>Status</th>
                        <th scope='col'>Change Status</th>
                        <th scope='col' className='text-end'>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedProjects.map((project, idx) => (
                        <tr key={project.id}>
                          <td className='text-muted'>{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                          <td>
                            <strong>{project.title}</strong>
                            <div className='small text-muted'>{project.id}</div>
                          </td>
                          <td>{project.owner}</td>
                          <td>{project.location}</td>
                          <td>{project.area}</td>
                          <td>{project.rateOfConstruction}</td>
                          <td>
                            <span
                              className={`badge rounded-pill px-3 py-2 ${
                                project.status === "pending"
                                  ? "text-bg-warning"
                                  : project.status === "in-progress"
                                  ? "text-bg-primary"
                                  : "text-bg-success"
                              }`}
                            >
                              {project.status}
                            </span>
                          </td>
                          <td style={{ minWidth: "190px" }}>
                            <select
                              className='form-select form-select-sm'
                              title='Change project status'
                              aria-label={`Change status for ${project.title}`}
                              value={project.status}
                              disabled={busy}
                              onChange={(event) => handleStatusChange(project, event.target.value as ProjectStatus)}
                            >
                              <option value='pending'>Pending</option>
                              <option value='in-progress'>In Progress</option>
                              <option value='completed'>Completed</option>
                            </select>
                          </td>
                          <td className='text-end'>
                            <div className='d-inline-flex gap-2'>
                              <button
                                type='button'
                                className='btn btn-outline-primary btn-sm admin-icon-btn'
                                onClick={() => setSelectedProject(project)}
                                aria-label={`View details for ${project.title}`}
                                title='View details'
                              >
                                <i className='bi bi-eye'></i>
                              </button>
                              <button
                                type='button'
                                className='btn btn-outline-secondary btn-sm admin-icon-btn'
                                onClick={() => navigate(`/admin/projects/${project.id}/edit`)}
                                aria-label={`Edit ${project.title}`}
                                title='Edit project'
                                disabled={busy}
                              >
                                <i className='bi bi-pencil'></i>
                              </button>
                              <button
                                type='button'
                                className='btn btn-outline-danger btn-sm admin-icon-btn'
                                onClick={() => handleDelete(project)}
                                aria-label={`Delete ${project.title}`}
                                title='Delete project'
                                disabled={busy}
                              >
                                <i className='bi bi-trash'></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {filteredProjects.length === 0 ? <div className='alert alert-light border mt-4 mb-0'>No projects match the current filters.</div> : null}
              </>
            ) : null}
          </div>
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredProjects.length}
            pageSize={PAGE_SIZE}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      <Modal show={Boolean(selectedProject)} onHide={() => setSelectedProject(null)} centered size='lg' contentClassName='border-0 shadow-lg overflow-hidden'>
        {selectedProject && (() => {
          const summary = getProjectProgressSummary(selectedProject);
          const statusMeta =
            selectedProject.status === "completed"
              ? { bg: "#dcfce7", color: "#15803d", border: "#86efac", label: "Completed" }
              : selectedProject.status === "in-progress"
              ? { bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd", label: "In Progress" }
              : { bg: "#fef3c7", color: "#b45309", border: "#fcd34d", label: "Pending" };
          const onTrack = Math.max(0, summary.totalTasks - summary.completedTasks - summary.delayedTasks);
          const createdLabel = selectedProject.createdAt
            ? new Date(selectedProject.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
            : "—";

          return (
            <Modal.Body style={{ padding: 0 }}>
              {/* ── Hero header ── */}
              <div style={{ background: "linear-gradient(135deg, #0c2420 0%, #134e4a 55%, #0d9488 100%)", padding: "22px 24px 18px", position: "relative" }}>
                <button
                  type='button'
                  onClick={() => setSelectedProject(null)}
                  aria-label='Close'
                  style={{ position: "absolute", top: 12, right: 14, background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff" }}
                >
                  <i className='bi bi-x-lg' style={{ fontSize: "0.85rem" }} />
                </button>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, paddingRight: 36 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: "0.62rem", fontWeight: 700, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 5 }}>
                      <i className='bi bi-kanban-fill' style={{ marginRight: 5 }} />{selectedProject.id}
                    </div>
                    <h5 style={{ color: "#fff", fontWeight: 800, fontSize: "1.15rem", margin: "0 0 6px", lineHeight: 1.25, wordBreak: "break-word" }}>
                      {selectedProject.title}
                    </h5>
                    {selectedProject.location && (
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <i className='bi bi-geo-alt-fill' style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.75rem" }} />
                        <span style={{ color: "rgba(255,255,255,0.72)", fontSize: "0.8rem" }}>{selectedProject.location}</span>
                      </div>
                    )}
                  </div>
                  <span style={{ flexShrink: 0, background: statusMeta.bg, color: statusMeta.color, border: `1.5px solid ${statusMeta.border}`, borderRadius: 8, padding: "4px 12px", fontSize: "0.72rem", fontWeight: 700, whiteSpace: "nowrap" }}>
                    {statusMeta.label}
                  </span>
                </div>
              </div>

              {/* ── Body ── */}
              <div style={{ padding: "18px 22px 4px" }}>

                {/* Info cards row */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 14 }}>
                  {[
                    { icon: "bi bi-person-fill", label: "Owner", value: selectedProject.owner || "—" },
                    { icon: "bi bi-rulers", label: "Area", value: selectedProject.area || "—" },
                    { icon: "bi bi-currency-rupee", label: "Rate / sq ft", value: selectedProject.rateOfConstruction || "—" },
                  ].map(({ icon, label, value }) => (
                    <div key={label} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
                        <i className={icon} style={{ color: "#0d9488", fontSize: "0.78rem" }} />
                        <span style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#94a3b8" }}>{label}</span>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: "0.86rem", color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={value}>
                        {value}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Contact info (if present) */}
                {(selectedProject.ownerEmail || selectedProject.clientPhone) && (
                  <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                    {selectedProject.ownerEmail && (
                      <div style={{ flex: 1, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "8px 12px", display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        <i className='bi bi-envelope-fill' style={{ color: "#0d9488", fontSize: "0.78rem", flexShrink: 0 }} />
                        <span style={{ fontSize: "0.8rem", color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedProject.ownerEmail}</span>
                      </div>
                    )}
                    {selectedProject.clientPhone && (
                      <div style={{ flex: 1, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "8px 12px", display: "flex", alignItems: "center", gap: 8 }}>
                        <i className='bi bi-telephone-fill' style={{ color: "#0d9488", fontSize: "0.78rem", flexShrink: 0 }} />
                        <span style={{ fontSize: "0.8rem", color: "#334155" }}>{selectedProject.clientPhone}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Task progress section */}
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "14px 16px", marginBottom: 14 }}>
                  <div style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#64748b", marginBottom: 12 }}>
                    Task Progress
                  </div>
                  {summary.totalTasks === 0 ? (
                    <div style={{ fontSize: "0.82rem", color: "#94a3b8", display: "flex", alignItems: "center", gap: 6 }}>
                      <i className='bi bi-info-circle' /> No tasks assigned yet
                    </div>
                  ) : (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 14 }}>
                        {[
                          { label: "Total", value: summary.totalTasks, color: "#475569" },
                          { label: "Done", value: summary.completedTasks, color: "#16a34a" },
                          { label: "Delayed", value: summary.delayedTasks, color: "#dc2626" },
                          { label: "On Track", value: onTrack, color: "#0d9488" },
                        ].map(({ label, value, color }) => (
                          <div key={label} style={{ textAlign: "center", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 4px" }}>
                            <div style={{ fontSize: "1.2rem", fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
                            <div style={{ fontSize: "0.6rem", color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 3 }}>{label}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {[
                          { label: "Planned", pct: summary.averagePlanned, bar: "#93c5fd" },
                          { label: "Actual", pct: summary.averageActual, bar: summary.averageActual >= summary.averagePlanned ? "#4ade80" : "#0d9488" },
                        ].map(({ label, pct, bar }) => (
                          <div key={label}>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.72rem", color: "#64748b", marginBottom: 4, fontWeight: 600 }}>
                              <span>{label}</span><span style={{ color: "#0f172a" }}>{pct}%</span>
                            </div>
                            <div style={{ height: 7, background: "#e2e8f0", borderRadius: 99, overflow: "hidden" }}>
                              <div style={{ height: "100%", width: `${pct}%`, background: bar, borderRadius: 99, transition: "width 0.5s ease" }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Description */}
                {selectedProject.description && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#64748b", marginBottom: 6 }}>Description</div>
                    <p style={{ margin: 0, fontSize: "0.84rem", color: "#334155", lineHeight: 1.65, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 14px" }}>
                      {selectedProject.description}
                    </p>
                  </div>
                )}

                {/* Contract files */}
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#64748b", marginBottom: 8 }}>Contract Files</div>
                  {selectedProject.contractFiles.length > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {selectedProject.contractFiles.map((file) => (
                        <span key={file} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 11px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, fontSize: "0.78rem", fontWeight: 600, color: "#15803d" }}>
                          <i className='bi bi-file-earmark-text' />{file}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: "0.82rem", color: "#94a3b8", display: "flex", alignItems: "center", gap: 6 }}>
                      <i className='bi bi-file-earmark-x' /> No contract files uploaded
                    </div>
                  )}
                </div>
              </div>

              {/* ── Footer ── */}
              <div style={{ borderTop: "1px solid #f1f5f9", background: "#f8fafc", padding: "12px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "0.72rem", color: "#94a3b8", display: "flex", alignItems: "center", gap: 5 }}>
                  <i className='bi bi-calendar3' /> Created {createdLabel}
                </span>
                <div style={{ display: "flex", gap: 8 }}>
                  <button type='button' className='btn btn-sm btn-outline-secondary' onClick={() => setSelectedProject(null)}>Close</button>
                  <button
                    type='button'
                    className='btn btn-sm'
                    style={{ background: "linear-gradient(135deg, #0d9488, #0f766e)", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600 }}
                    onClick={() => { setSelectedProject(null); navigate(`/admin/projects/${selectedProject.id}/edit`); }}
                    disabled={busy}
                  >
                    <i className='bi bi-pencil me-1' /> Edit Project
                  </button>
                </div>
              </div>
            </Modal.Body>
          );
        })()}
      </Modal>
    </section>
  );
};

export default ProjectListPage;
